import * as MenuPrimitive from 'radix-ui/dropdown-menu';
import { type ReactNode, useId } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { MENU_SURFACE, MENU_VIEWPORT } from '../SelectMenu/SelectMenu';

/**
 * Action menu. Items are 40px tall on every viewport - a menu is the one place
 * where a mis-tap performs the wrong action rather than nothing.
 */
export type DropdownMenuWidth = 'default' | 'prose';

export interface DropdownMenuProps {
  readonly trigger: ReactNode;
  readonly children: ReactNode;
  readonly align?: 'start' | 'end';
  /** `prose` for a menu holding a sentence rather than a list of labels. */
  readonly width?: DropdownMenuWidth | undefined;
  /**
   * NO `className` (`ui-foundation.md` D1). A floating or chrome primitive owns its width,
   * its padding, its max-height and its collision behaviour; a caller that reaches into any
   * of them is the caller who eventually gets one of them wrong. Placement belongs to the
   * ROW the control sits in, so wrap it in a box and give the box the width.
   */
}

/**
 * `data-highlighted` is Radix's ONE highlight, and it covers the keyboard and the
 * hovering pointer - neither of which a thumb has. `pressable` is the touch half:
 * the item darkens one step past the highlight the moment it is pressed and eases
 * back on release (index.css). The two stack rather than compete - a pointer that
 * presses a highlighted item goes highlight -> pressed, which is what a native
 * menu does.
 */
const ITEM =
  'pressable flex min-h-(--touch-target-min) items-center gap-2.5 rounded-control px-2.5 text-sm ' +
  'text-ink outline-none select-none data-highlighted:bg-surface-hover ' +
  'data-disabled:opacity-50';

/**
 * A MENU THAT CARRIES A SENTENCE IS BOUNDED (`ui-foundation.md` D1). `MENU_SURFACE`'s
 * `min-w-52` is a floor and a menu otherwise takes the width of its widest child, so one
 * unwrapped footnote made the page-size menu 830px wide on a desktop and, at 375px, pushed
 * its own last words off the right edge of the screen. The caller says its menu has prose
 * in it; this component decides what that is worth in pixels.
 */
const WIDTHS: Record<DropdownMenuWidth, string> = {
  default: '',
  prose: 'w-64',
};

export function DropdownMenu({
  trigger,
  children,
  align = 'end',
  width = 'default',
}: DropdownMenuProps) {
  return (
    <MenuPrimitive.Root>
      <MenuPrimitive.Trigger asChild>{trigger}</MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cx(MENU_SURFACE, MENU_VIEWPORT, 'min-w-52 p-1', WIDTHS[width])}
        >
          {children}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

export interface DropdownItemProps {
  readonly onSelect: () => void;
  readonly children: ReactNode;
  readonly icon?: IconName | undefined;
  readonly disabled?: boolean | undefined;
  readonly tone?: 'default' | 'danger';
}

export function DropdownItem({
  onSelect,
  children,
  icon,
  disabled = false,
  tone = 'default',
}: DropdownItemProps) {
  return (
    <MenuPrimitive.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cx(ITEM, tone === 'danger' && 'text-danger')}
    >
      {icon !== undefined && <Icon name={icon} className="text-ink-subtle" />}
      {children}
    </MenuPrimitive.Item>
  );
}

export interface DropdownCheckboxItemProps {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly children: ReactNode;
  readonly disabled?: boolean | undefined;
}

export function DropdownCheckboxItem({
  checked,
  onCheckedChange,
  children,
  disabled = false,
}: DropdownCheckboxItemProps) {
  return (
    <MenuPrimitive.CheckboxItem
      checked={checked}
      disabled={disabled}
      // Keeping the menu open lets a user toggle several columns in one pass.
      onSelect={(event) => event.preventDefault()}
      onCheckedChange={onCheckedChange}
      className={ITEM}
    >
      {/* `items-center`: without it the indicator stretches and the tick lands
          on the row's text baseline instead of in the middle of the row. */}
      <span className="flex w-4 shrink-0 items-center justify-center text-accent">
        <MenuPrimitive.ItemIndicator>
          <Icon name="check" size="0.875rem" />
        </MenuPrimitive.ItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  );
}

export interface DropdownRadioGroupProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly children: ReactNode;
  /**
   * The group's heading, drawn as the menu's own label and BOUND to the group as its
   * accessible name. It is optional because a menu with one radio group needs no
   * heading; it stops being optional the moment there are two, which the appearance
   * menu is - three groups in one panel, two of which have an option called "Dark".
   * Without the binding a screen reader reads six unrelated radio items and a test
   * cannot tell one "Dark" from the other, which is the same ambiguity.
   */
  readonly label?: string | undefined;
}

/**
 * ONE choice out of a set, inside a menu - the shape a preference takes when it
 * has more than two states and a control cannot show them all at once. The
 * checkbox item above answers "is this on"; this one answers "which one", and a
 * screen reader is told which by the roles rather than by the tick alone.
 */
export function DropdownRadioGroup({
  value,
  onValueChange,
  children,
  label,
}: DropdownRadioGroupProps) {
  const id = useId();
  return (
    <MenuPrimitive.RadioGroup
      value={value}
      onValueChange={onValueChange}
      aria-labelledby={label === undefined ? undefined : id}
    >
      {label !== undefined && <DropdownLabel id={id}>{label}</DropdownLabel>}
      {children}
    </MenuPrimitive.RadioGroup>
  );
}

export interface DropdownRadioItemProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly icon?: IconName | undefined;
}

export function DropdownRadioItem({ value, children, icon }: DropdownRadioItemProps) {
  return (
    <MenuPrimitive.RadioItem value={value} className={ITEM}>
      {/* Same 16px gutter as the checkbox item, so a menu that mixes the two
          keeps one text column. `items-center` for the same reason it is there:
          without it the indicator stretches and the tick lands on the baseline. */}
      <span className="flex w-4 shrink-0 items-center justify-center text-accent">
        <MenuPrimitive.ItemIndicator>
          <Icon name="check" size="0.875rem" />
        </MenuPrimitive.ItemIndicator>
      </span>
      {icon !== undefined && <Icon name={icon} className="text-ink-subtle" />}
      {children}
    </MenuPrimitive.RadioItem>
  );
}

export function DropdownLabel({
  children,
  id,
}: {
  readonly children: ReactNode;
  /** Set when a group points at this label with `aria-labelledby`. */
  readonly id?: string | undefined;
}) {
  return (
    <MenuPrimitive.Label
      id={id}
      className="px-2.5 py-2 text-2xs font-semibold tracking-widest text-ink-subtle uppercase"
    >
      {children}
    </MenuPrimitive.Label>
  );
}

export function DropdownSeparator() {
  return <MenuPrimitive.Separator className="my-1 h-px bg-line" />;
}

export interface DropdownSubProps {
  /** The submenu's title: the name of the setting or the term its items share. */
  readonly label: string;
  /** The submenu's items, typically one `DropdownRadioGroup` for a setting with several values. */
  readonly children: ReactNode;
}

/**
 * A SUBMENU, ADDED BY BR-SETTINGS (2026-10-05) for the account menu's Appearance settings
 * (register A8-7: "one submenu per setting with check-marked items, never a pop-up button inside a
 * menu"). Additive: no existing caller of this file changes.
 *
 * hig: https://developer.apple.com/design/human-interface-guidelines/menus, "Submenus": "A menu
 * item indicates the presence of a submenu by displaying a symbol - like a chevron - after its
 * label. Submenus are functionally identical to menus, aside from their hierarchical
 * positioning", restricted "to a single level"; "Toggled items": "Consider using a checkmark to
 * show that an attribute is currently in effect."
 *
 * kit: Apple macOS 27 UI Kit.sketch / Menus/Light/Regular/Menu with Selection/Items - Submenu/1 -
 * Idle and 3 -Open: in a menu whose items carry the checkmark column the submenu row's title
 * starts past that column (x 12 of 160, where the menu without selection starts it at x 0), the
 * chevron stands trailing (x 148), and the open row keeps its highlight. So the row is this
 * file's item with the same 16px tick gutter `DropdownRadioItem` leaves, and the panel it opens
 * is the same surface as its parent (`ContextMenuSub` is the context menu's twin).
 */
export function DropdownSub({ label, children }: DropdownSubProps) {
  const id = useId();
  return (
    <MenuPrimitive.Sub>
      <MenuPrimitive.SubTrigger id={id} className={cx(ITEM, 'data-[state=open]:bg-surface-hover')}>
        <span aria-hidden="true" className="w-4 shrink-0" />
        <span className="flex-1">{label}</span>
        <Icon name="chevron-right" size="0.875rem" className="text-ink-subtle" />
      </MenuPrimitive.SubTrigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.SubContent
          collisionPadding={8}
          sideOffset={2}
          className={cx(MENU_SURFACE, MENU_VIEWPORT, 'min-w-52 p-1')}
          aria-labelledby={id}
        >
          {children}
        </MenuPrimitive.SubContent>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Sub>
  );
}
